import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { Input } from "~/components/ui/input";
import { api } from "~/lib/api";
import { formatKES, toNum } from "~/lib/format";
import type { ShipMode } from "~/lib/modes";
import { cn } from "~/lib/utils";

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

function decimal(raw: string): string {
  return raw.replace(/[^\d.]/g, "");
}

export function ShippingEstimate({ origin, mode }: { origin: string; mode: ShipMode }) {
  const [open, setOpen] = useState(false);
  const [weight, setWeight] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");

  const kg = Number(useDebounced(weight, 350)) || 0;
  const l = Number(useDebounced(length, 350)) || 0;
  const w = Number(useDebounced(width, 350)) || 0;
  const h = Number(useDebounced(height, 350)) || 0;
  const hasBox = l > 0 && w > 0 && h > 0;

  const quote = useQuery({
    queryKey: ["quote", origin, mode, kg, l, w, h],
    queryFn: () =>
      api.quote({
        originCountry: origin,
        mode,
        weightKg: kg,
        lengthCm: hasBox ? l : undefined,
        widthCm: hasBox ? w : undefined,
        heightCm: hasBox ? h : undefined,
      }),
    enabled: open && (kg > 0 || hasBox),
    retry: false,
  });

  return (
    <div className="border-hairline bg-surface rounded-lg border">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-14 w-full items-center justify-between gap-3 px-4 text-left"
      >
        <span>
          <span className="block text-[15px] font-medium">Estimate the shipping cost</span>
          <span className="text-muted-foreground block text-[13px]">Optional. Enter a weight or box size.</span>
        </span>
        <ChevronDown className={cn("text-muted-foreground size-5 shrink-0 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="space-y-3 px-4 pb-4">
          <Input
            density="comfortable"
            inputMode="decimal"
            aria-label="Weight in kilograms"
            placeholder="Weight in kg, for example 2.5"
            value={weight}
            onChange={(e) => setWeight(decimal(e.target.value))}
          />
          <div className="grid grid-cols-3 gap-2">
            <Input density="comfortable" inputMode="decimal" aria-label="Box length in centimetres" placeholder="Length" value={length} onChange={(e) => setLength(decimal(e.target.value))} />
            <Input density="comfortable" inputMode="decimal" aria-label="Box width in centimetres" placeholder="Width" value={width} onChange={(e) => setWidth(decimal(e.target.value))} />
            <Input density="comfortable" inputMode="decimal" aria-label="Box height in centimetres" placeholder="Height" value={height} onChange={(e) => setHeight(decimal(e.target.value))} />
          </div>
          <p className="text-muted-foreground text-[13px]">Box size in centimetres, if you know it.</p>
          {quote.data && (
            <div className="border-hairline border-t pt-3" aria-live="polite">
              <p className="text-muted-foreground text-[13px]">Estimated cost</p>
              <p className="tabular text-[28px] leading-tight font-semibold tracking-[-0.02em]">{formatKES(quote.data.estimateKes)}</p>
              <p className="text-muted-foreground type-footnote mt-1">
                {toNum(quote.data.lowKes) > 0 && `Likely ${formatKES(quote.data.lowKes)} to ${formatKES(quote.data.highKes)}. `}
                We set the final price after weighing it on arrival.
              </p>
            </div>
          )}
          {quote.isError && <p className="text-destructive type-footnote">We could not work out an estimate. Check the numbers and try again.</p>}
        </div>
      )}
    </div>
  );
}
